/**
 * Reference-data API helpers — PMA-BE-074..077 (backend), PMA-REF wave.
 *
 * Backs the admin-configurable dropdowns: player_position, season,
 * competition_stage, match_type, venue_surface. All calls go through the
 * shared apiClient (axios + Firebase ID-token interceptor). Reads are
 * available to any authenticated role; mutations require admin (RBAC is
 * enforced server-side — this client makes no client-side role check).
 *
 * This is the ONLY module that talks to /api/v1/reference-data. Every
 * consumer goes through the `useReferenceValues` hook (`hooks/useReferenceValues.ts`),
 * never this module directly.
 */

import { apiClient } from "./api";

// ── Types ────────────────────────────────────────────────────────────────────

export type ReferenceCategory =
  | "player_position"
  | "season"
  | "competition_stage"
  | "match_type"
  | "venue_surface";

export const REFERENCE_CATEGORIES: ReferenceCategory[] = [
  "player_position",
  "season",
  "competition_stage",
  "match_type",
  "venue_surface",
];

export type PositionGroup =
  | "goalkeeper"
  | "defense"
  | "midfield"
  | "attack"
  | "other";

export interface ReferenceLabels {
  en: string;
  es: string;
}

export interface ReferenceValue {
  _id: string;
  category: ReferenceCategory;
  code: string;
  labels: ReferenceLabels;
  /** Required (non-null) only for category === "player_position". */
  group: PositionGroup | null;
  sort_order: number;
  is_active: boolean;
  is_system: boolean;
  /** True only for values a backend code path depends on literally (e.g. match_type). Deactivation is always rejected (400) while true. */
  is_locked: boolean;
  created_at: string;
  updated_at: string;
}

export interface ReferenceValueCreatePayload {
  category: ReferenceCategory;
  code: string;
  labels: ReferenceLabels;
  /** Required for player_position, must be omitted for every other category. */
  group?: PositionGroup;
  sort_order?: number;
}

export interface ReferenceValueUpdatePayload {
  labels?: ReferenceLabels;
  sort_order?: number;
  is_active?: boolean;
}

// ── API functions ─────────────────────────────────────────────────────────────

/** GET /api/v1/reference-data — all 5 categories in one call. */
export async function getAllReferenceValues(
  includeInactive = false,
): Promise<Record<ReferenceCategory, ReferenceValue[]>> {
  return apiClient.get<Record<ReferenceCategory, ReferenceValue[]>>(
    "/reference-data",
    { params: { include_inactive: includeInactive } },
  );
}

/** GET /api/v1/reference-data/{category} */
export async function getReferenceValues(
  category: ReferenceCategory,
  includeInactive = false,
): Promise<ReferenceValue[]> {
  return apiClient.get<ReferenceValue[]>(`/reference-data/${category}`, {
    params: { include_inactive: includeInactive },
  });
}

/** POST /api/v1/reference-data — admin only. */
export async function createReferenceValue(
  payload: ReferenceValueCreatePayload,
): Promise<ReferenceValue> {
  return apiClient.post<ReferenceValue>("/reference-data", payload);
}

/** PATCH /api/v1/reference-data/{value_id} — admin only. */
export async function updateReferenceValue(
  valueId: string,
  payload: ReferenceValueUpdatePayload,
): Promise<ReferenceValue> {
  return apiClient.patch<ReferenceValue>(`/reference-data/${valueId}`, payload);
}

/** PUT /api/v1/reference-data/{category}/reorder — admin only. `orderedIds` must be the full id set for that category. */
export async function reorderReferenceValues(
  category: ReferenceCategory,
  orderedIds: string[],
): Promise<ReferenceValue[]> {
  return apiClient.put<ReferenceValue[]>(
    `/reference-data/${category}/reorder`,
    { ordered_ids: orderedIds },
  );
}
